import { Fragment, lazy, Suspense } from 'react';
import MainLayout from '../layouts/MainLayout';
import BlankLayout from '../layouts/BlankLayout';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './ProtectedRoute';
import PermissionRoute from './PermissionRoute';
import { SUPPORTED_LANGUAGES } from '../i18nLanguages';

const Login = lazy(() => import('../pages/admin/login/Login'));
const Dashboard = lazy(() => import('../pages/home/Dashboard'));
const Definitions = lazy(() => import('../pages/definitions/Definitions'));
const ConstantsList = lazy(
  () => import('../pages/definitions/constants/ConstantsList')
);
const Reports = lazy(() => import('../pages/reports/Reports'));
const JobRepairsReport = lazy(
  () => import('../pages/reports/jobRepairsReport/JobRepairsReport')
);
const JobRepairsAgingReport = lazy(
  () => import('../pages/reports/jobRepairsAgingReport/JobRepairsAgingReport')
);
const ShippingReport = lazy(
  () => import('../pages/reports/shippingReport/ShippingReport')
);
const ExternalRepairsReport = lazy(
  () => import('../pages/reports/externalRepairsReport/ExternalRepairsReport')
);
const JobRepairsList = lazy(
  () => import('../pages/jobRepairs/JobRepairsList')
);
const JobRepairEdit = lazy(
  () => import('../pages/jobRepairs/jobRepairEdit/JobRepairEdit')
);
const JobRepairSettings = lazy(
  () => import('../pages/jobRepairSettings/JobRepairSettings')
);
const ActivityLogList = lazy(
  () => import('../pages/activityLog/ActivityLogList')
);
const UsersList = lazy(
  () => import('../pages/usersPermissions/users/UsersList')
);
const PermissionsList = lazy(
  () => import('../pages/usersPermissions/permissions/PermissionsList')
);
const PermissionsEdit = lazy(
  () =>
    import(
      '../pages/usersPermissions/permissions/permissionsEdit/PermissionsEdit'
    )
);
const Profile = lazy(() => import('../pages/profile/Profile'));
const ClientsList = lazy(() => import('../pages/clients/ClientsList'));
const ForgotPassword = lazy(
  () => import('../pages/admin/forgotPassword/ForgotPassword')
);
const AcceptInvite = lazy(
  () => import('../pages/admin/acceptInvite/AcceptInvite')
);
const Setup2FaPage = lazy(
  () => import('../pages/admin/setup2faPage/Setup2FaPage')
);
const Verify2FaPage = lazy(
  () => import('../pages/admin/verify2faPage/Verify2FaPage')
);
const JobRepairTrack = lazy(() => import('../pages/jobRepairTrack/JobRepairTrack'));
const NotFound = lazy(() => import('../pages/notFound/NotFound'));

const renderRouteSet = (prefix = '') => {
  return (
    <Fragment key={prefix || 'default'}>
      <Route element={<BlankLayout />}>
        <Route path={`${prefix}/login`} element={<Login />} />
        <Route
          path={`${prefix}/forgot_password`}
          element={<ForgotPassword />}
        />
        <Route
          path={`${prefix}/track/:trackingKey`}
          element={<JobRepairTrack />}
        />
        <Route
          path={`${prefix}/invite/accept/:accessToken`}
          element={<AcceptInvite />}
        />
      </Route>
      <Route
        element={
          <ProtectedRoute>
            <MainLayout fullPage />
          </ProtectedRoute>
        }
      >
        <Route path={`${prefix}/dashboard`} element={
            <PermissionRoute permission="useDashboard">
              <Dashboard />
            </PermissionRoute>
          } />
        <Route
          path={`${prefix}/job_repairs`}
          element={
            <PermissionRoute permission="viewJobRepairs">
              <JobRepairsList />
            </PermissionRoute>
          }
        />
        <Route
          path={`${prefix}/job_repairs/deleted`}
          element={
            <PermissionRoute permission="viewDeletedJobRepairs">
              <JobRepairsList deleted />
            </PermissionRoute>
          }
        />
        <Route
          path={`${prefix}/job_repairs/edit`}
          element={
            <PermissionRoute permission="addJobRepairs">
              <JobRepairEdit />
            </PermissionRoute>
          }
        />
        <Route
          path={`${prefix}/job_repairs/edit/:idKey`}
          element={
            <PermissionRoute permission="editJobRepairs">
              <JobRepairEdit />
            </PermissionRoute>
          }
        />
        <Route
          path={`${prefix}/definitions`}
          element={
            <PermissionRoute permission="useDefinitions">
              <Definitions />
            </PermissionRoute>
          }
        />
        <Route
          path={`${prefix}/constants/:constantType`}
          element={
            <PermissionRoute permission="viewConstants">
              <ConstantsList />
            </PermissionRoute>
          }
        />
        <Route
          path={`${prefix}/reports`}
          element={
            <PermissionRoute permission="useReports">
              <Reports />
            </PermissionRoute>
          }
        />
        <Route
          path={`${prefix}/report_job_repairs`}
          element={
            <PermissionRoute permission="viewReportGeneral">
              <JobRepairsReport />
            </PermissionRoute>
          }
        />
        <Route
          path={`${prefix}/report_job_repairs_aging`}
          element={
            <PermissionRoute permission="viewReportAging">
              <JobRepairsAgingReport />
            </PermissionRoute>
          }
        />
        <Route
          path={`${prefix}/report_shipping`}
          element={
            <PermissionRoute permission="viewReportShipping">
              <ShippingReport />
            </PermissionRoute>
          }
        />
        <Route
          path={`${prefix}/report_external_repairs`}
          element={
            <PermissionRoute permission="viewReportExternal">
              <ExternalRepairsReport />
            </PermissionRoute>
          }
        />
        <Route
          path={`${prefix}/activity_log`}
          element={
            <PermissionRoute permission="useActivityLog">
              <ActivityLogList />
            </PermissionRoute>
          }
        />
        <Route
          path={`${prefix}/users`}
          element={
            <PermissionRoute permission="viewUsers">
              <UsersList />
            </PermissionRoute>
          }
        />
        <Route
          path={`${prefix}/permissions`}
          element={
            <PermissionRoute permission="viewPermissions">
              <PermissionsList />
            </PermissionRoute>
          }
        />
        <Route
          path={`${prefix}/permissions/edit`}
          element={
            <PermissionRoute permission="addPermissions">
              <PermissionsEdit />
            </PermissionRoute>
          }
        />
        <Route
          path={`${prefix}/permissions/edit/:idKey`}
          element={
            <PermissionRoute permission="editPermissions">
              <PermissionsEdit />
            </PermissionRoute>
          }
        />
        <Route
          path={`${prefix}/permissions/compare`}
          element={
            <PermissionRoute permission="viewCompare">
              <PermissionsEdit compare />
            </PermissionRoute>
          }
        />
        <Route
          path={`${prefix}/clients`}
          element={
            <PermissionRoute platformOnly>
              <ClientsList />
            </PermissionRoute>
          }
        />
        <Route
          path={`${prefix}/job_repair_settings`}
          element={
            <PermissionRoute permission="useJobRepairSettings">
              <JobRepairSettings />
            </PermissionRoute>
          }
        />
      </Route>
      <Route
        path={`${prefix}/profile`}
        element={
          <ProtectedRoute>
            <MainLayout fullPage />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="edit" replace />} />
        <Route path="edit" element={<Profile />} />
      </Route>
      <Route
        path={`${prefix}/profile`}
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route path="set_up_2_step_verification" element={<Setup2FaPage />} />
        <Route
          path="turn_off_2_step_verification"
          element={<Verify2FaPage isDisabled />}
        />
      </Route>
      <Route
        path={`${prefix}/home`}
        element={<Navigate to={`${prefix}/dashboard`} replace />}
      />
      <Route
        path={prefix || '/'}
        element={<Navigate to={`${prefix}/dashboard`} replace />}
      />
      <Route element={<BlankLayout />}>
        <Route path={`${prefix}/*`} element={<NotFound />} />
      </Route>
    </Fragment>
  );
};

const AppRoutes = () => {
  return (
    <Suspense fallback={null}>
      <Routes>
        {renderRouteSet()}
        {SUPPORTED_LANGUAGES.map((language) => renderRouteSet(`/${language}`))}
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
